import moment from "moment";

const ISO = "YYYY-MM-DD";

/**
 * Brzi izbori perioda za mobilne izveštaje (nedelja počinje ponedeljkom).
 * @param {moment.Moment|Date|string} [today]
 * @returns {Array<{ key: string, from: string, to: string }>}
 */
export const buildPeriodPresets = (today = moment()) => {
  const day = moment(today).startOf("day");
  const range = (from, to) => ({ from: from.format(ISO), to: to.format(ISO) });
  const tomorrow = day.clone().add(1, "day");
  const nextWeek = day.clone().add(1, "week");
  const lastMonth = day.clone().subtract(1, "month");

  return [
    { key: "today", ...range(day, day) },
    { key: "tomorrow", ...range(tomorrow, tomorrow) },
    {
      key: "this_week",
      ...range(day.clone().startOf("isoWeek"), day.clone().endOf("isoWeek")),
    },
    {
      key: "next_week",
      ...range(nextWeek.clone().startOf("isoWeek"), nextWeek.clone().endOf("isoWeek")),
    },
    {
      key: "this_month",
      ...range(day.clone().startOf("month"), day.clone().endOf("month")),
    },
    {
      key: "last_month",
      ...range(lastMonth.clone().startOf("month"), lastMonth.clone().endOf("month")),
    },
  ];
};

/**
 * Brzi izbori perioda za izveštaj gostiju: gleda se unazad (juče, prošla nedelja), jer su to
 * isporučene narudžbe, a ne obroci koji tek stižu.
 * @param {moment.Moment|Date|string} [today]
 * @returns {Array<{ key: string, from: string, to: string }>}
 */
export const buildGuestPeriodPresets = (today = moment()) => {
  const day = moment(today).startOf("day");
  const range = (from, to) => ({ from: from.format(ISO), to: to.format(ISO) });
  const yesterday = day.clone().subtract(1, "day");
  const lastWeek = day.clone().subtract(1, "week");
  const lastMonth = day.clone().subtract(1, "month");

  return [
    { key: "today", ...range(day, day) },
    { key: "yesterday", ...range(yesterday, yesterday) },
    {
      key: "this_week",
      ...range(day.clone().startOf("isoWeek"), day.clone().endOf("isoWeek")),
    },
    {
      key: "last_week",
      ...range(lastWeek.clone().startOf("isoWeek"), lastWeek.clone().endOf("isoWeek")),
    },
    {
      key: "this_month",
      ...range(day.clone().startOf("month"), day.clone().endOf("month")),
    },
    {
      key: "last_month",
      ...range(lastMonth.clone().startOf("month"), lastMonth.clone().endOf("month")),
    },
  ];
};

/**
 * @param {Array<{ key: string, from: string, to: string }>} presets
 * @param {string} from
 * @param {string} to
 * @returns {string|null}
 */
export const findPresetKey = (presets, from, to) =>
  presets.find((preset) => preset.from === from && preset.to === to)?.key || null;

/**
 * "19.10." ili "19.10.2026."
 * @param {string} date
 * @param {boolean} [withYear]
 * @returns {string}
 */
export const formatShortDate = (date, withYear = false) =>
  moment(date, ISO).format(withYear ? "DD.MM.YYYY." : "DD.MM.");

/**
 * "19.10.2026." za jedan dan, "19.10. – 25.10.2026." za opseg.
 * @param {string} from
 * @param {string} to
 * @returns {string}
 */
export const formatRange = (from, to) => {
  if (!to || from === to) {
    return formatShortDate(from, true);
  }
  const sameYear = moment(from, ISO).year() === moment(to, ISO).year();
  return `${formatShortDate(from, !sameYear)} – ${formatShortDate(to, true)}`;
};

/**
 * Broj dana u opsegu, uključujući oba kraja.
 * @param {string} from
 * @param {string} to
 * @returns {number}
 */
export const countDays = (from, to) =>
  moment(to || from, ISO).diff(moment(from, ISO), "days") + 1;

/**
 * Vraća [from, to] sortirano; jedan izabran dan postaje opseg od jednog dana.
 * @param {string[]} dates
 * @returns {[string, string]}
 */
export const normalizeRange = (dates) => {
  const sorted = [...(dates || [])].filter(Boolean).sort();
  const from = sorted[0] || moment().format(ISO);
  return [from, sorted[sorted.length - 1] || from];
};
